import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import AddToHomeScreenIcon from "@mui/icons-material/AddToHomeScreen";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCurrentUser } from "../context/UserContext";
import {
  INSTALL_GUIDE_EVENT,
  detectPlatform,
  installSteps,
  isInstallGuideDismissed,
  isInstallGuideSnoozed,
  isStandalone,
  markInstallGuideDismissed,
  shouldOfferInstall,
  snoozeInstallGuide,
} from "../../helpers/pwa-install";

// ────────────────────────────────────────────────────────────────────────
// InstallAppGuide — "modo celular": cómo dejar SmartDx como app en el teléfono.
//
// El médico abre el link en el navegador del celular, toca Compartir → "Agregar
// a pantalla de inicio", y desde ahí SmartDx se abre a pantalla completa como
// cualquier app. Inicia sesión una vez y ve exactamente lo mismo que en el
// computador, porque los datos viven en su cuenta, no en el aparato.
//
// Se ofrece SOLO: en celular, cuando todavía NO está instalada, una vez, y sin
// tapar el tutorial del Modo Turno (que arranca solo en /turno). El chip
// "Instalar app" del menú mobile la reabre cuando quiera — ver `openInstallGuide`.
//
// Se mantiene a mano (portal + Box) en vez de un Dialog de MUI por la misma
// razón que TurnoTutorial: control total del stacking sobre el navbar fixed.
// ────────────────────────────────────────────────────────────────────────

const Z_SCRIM = 1690;
const Z_SHEET = 1691;
// #1660 R18: el tutorial "Tu primer documento" (TutorialPrimerDocGate, Z_TUTORIAL = 15500) cubre toda la
// pantalla; con el z de arriba esta hoja quedaba DEBAJO y el toque en "Ver cómo" de su pantalla final
// "no hacía nada" a la vista del médico. Con el tutorial abierto la hoja sube por encima de él, pero sigue
// bajo Términos y el gate de perfil (z 20000). Se detecta por el DOM al abrir (la guía vive en App y el
// tutorial en Turno: no comparten estado) y solo entonces; en el resto de la app el z no cambia.
// Cubre R18 — .claude/specs/1660-tutorial-primer-ingreso-computador/
const Z_SCRIM_SOBRE_TUTORIAL = 15600;
const Z_SHEET_SOBRE_TUTORIAL = 15601;
const hayTutorialAbierto = () =>
  typeof document !== "undefined" && document.querySelector('[data-testid="tutorial-primer-doc"]') != null;

// Espera antes de ofrecerla sola: que el médico alcance a ver dónde cayó.
const AUTO_DELAY_MS = 1800;

// Android/Chrome avisa "esto es instalable" con `beforeinstallprompt` y deja
// disparar el instalador nativo — pero el evento llega temprano y solo se puede
// usar el que se guardó. Por eso se captura en el módulo (se registra al importar
// el componente, mucho antes de que el usuario abra la guía) y no en un effect.
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent("smartdx:install-prompt-ready"));
  });
}

const ICONS = {
  share: IosShareIcon,
  home: AddToHomeScreenIcon,
  check: CheckCircleOutlineIcon,
  more: MoreVertIcon,
  open: OpenInNewIcon,
  copy: ContentCopyIcon,
};

function Step({ n, icon, text }) {
  const Icon = ICONS[icon] || CheckCircleOutlineIcon;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          flex: "0 0 auto",
          width: 30,
          height: 30,
          borderRadius: "999px",
          display: "grid",
          placeItems: "center",
          fontSize: 13,
          fontWeight: 700,
          color: "var(--text-on-primary)",
          backgroundColor: "var(--color-primary)",
        }}
      >
        {n}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: "4px" }}>
        <Icon sx={{ fontSize: 18, color: "var(--color-primary-soft)", flex: "0 0 auto" }} />
        <Box sx={{ fontSize: 14.5, lineHeight: 1.45, color: "var(--text-secondary)" }}>{text}</Box>
      </Box>
    </Box>
  );
}

export function InstallAppSheet({ open, onClose, onDone, platform }) {
  const [copied, setCopied] = useState(false);
  const [canPrompt, setCanPrompt] = useState(Boolean(deferredPrompt));

  useEffect(() => {
    const onReady = () => setCanPrompt(true);
    window.addEventListener("smartdx:install-prompt-ready", onReady);
    return () => window.removeEventListener("smartdx:install-prompt-ready", onReady);
  }, []);

  // Cerrar con Escape, igual que cualquier diálogo de la app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const guide = useMemo(() => installSteps(platform), [platform]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  }, []);

  const nativeInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    const prompt = deferredPrompt;
    deferredPrompt = null;
    setCanPrompt(false);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") onDone?.();
    } catch {
      /* si el navegador lo rechaza quedan los pasos manuales a la vista */
    }
  }, [onDone]);

  if (!open) return null;

  const sobreTutorial = hayTutorialAbierto();
  const zScrim = sobreTutorial ? Z_SCRIM_SOBRE_TUTORIAL : Z_SCRIM;
  const zSheet = sobreTutorial ? Z_SHEET_SOBRE_TUTORIAL : Z_SHEET;
  const showNativeInstall = canPrompt && guide.variant === "android";
  const showCopy = guide.variant === "ios-otro-navegador";
  // "Ya la agregué" solo tiene sentido cuando los pasos TERMINAN en el ícono
  // instalado. En el navegador embebido o en escritorio el paso final es otro
  // (salir a Safari, abrirla en el celular): ahí el botón solo acusa recibo.
  const canFinishHere = guide.variant === "ios" || guide.variant === "android";
  const primaryLabel = canFinishHere ? "Listo, ya la agregué" : "Entendido";

  return createPortal(
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: zScrim,
          backgroundColor: "rgba(var(--shadow-rgb),0.55)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
        }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Instalar SmartDx en el celular"
        sx={{
          position: "fixed",
          zIndex: zSheet,
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 0,
          width: "100%",
          maxWidth: 480,
          px: 2.5,
          pt: 2.25,
          pb: "calc(20px + env(safe-area-inset-bottom))",
          borderRadius: "20px 20px 0 0",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--fg-rgb),0.12)",
          borderBottom: "none",
          boxShadow: "0 -18px 50px rgba(var(--shadow-rgb),0.5)",
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        {/* Agarradera: le dice al pulgar que esto es una hoja que se puede cerrar */}
        <Box
          sx={{
            width: 38,
            height: 4,
            borderRadius: "999px",
            backgroundColor: "rgba(var(--fg-rgb),0.22)",
            mx: "auto",
            mb: 1.75,
          }}
        />

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--color-primary-soft)",
                mb: 0.75,
              }}
            >
              SmartDx en tu celular
            </Box>
            <Box
              component="h2"
              sx={{
                m: 0,
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
              }}
            >
              {guide.title}
            </Box>
          </Box>
          <Box
            onClick={onClose}
            role="button"
            aria-label="Cerrar"
            sx={{
              flex: "0 0 auto",
              width: 32,
              height: 32,
              display: "grid",
              placeItems: "center",
              borderRadius: "999px",
              cursor: "pointer",
              color: "var(--text-muted)",
              "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.08)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 19 }} />
          </Box>
        </Box>

        <Box sx={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)", mt: 1, mb: 2.25 }}>
          {guide.intro}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {guide.steps.map((s, i) => (
            <Step key={s.text} n={i + 1} icon={s.icon} text={s.text} />
          ))}
        </Box>

        {/* El cierre del asunto: por qué vale la pena instalarla. */}
        <Box
          sx={{
            mt: 2.25,
            p: 1.5,
            borderRadius: "12px",
            border: "1px solid rgba(var(--color-primary-rgb),0.28)",
            backgroundColor: "rgba(var(--color-primary-rgb),0.10)",
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--text-secondary)",
          }}
        >
          Después inicias sesión una sola vez. Es la misma cuenta y los mismos pacientes del
          computador: lo que escribes en el celular ya está allá, sin sincronizar nada a mano.
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 2.25 }}>
          {showNativeInstall && (
            <Button
              onClick={nativeInstall}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                py: 1.1,
                color: "var(--text-on-primary)",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary-soft)" },
              }}
            >
              Instalar ahora
            </Button>
          )}

          {showCopy && (
            <Button
              onClick={copyLink}
              startIcon={<ContentCopyIcon sx={{ fontSize: 17 }} />}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                py: 1.1,
                color: "var(--text-on-primary)",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary-soft)" },
              }}
            >
              {copied ? "Link copiado" : "Copiar link"}
            </Button>
          )}

          {!showNativeInstall && !showCopy && (
            <Button
              onClick={onDone}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                py: 1.1,
                color: "var(--text-on-primary)",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary-soft)" },
              }}
            >
              {primaryLabel}
            </Button>
          )}

          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "999px",
              px: 2,
              color: "var(--text-muted)",
              "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.08)" },
            }}
          >
            Ahora no
          </Button>
        </Box>
      </Box>
    </>,
    document.body,
  );
}

// ¿El usuario ya pasó los overlays obligatorios del onboarding? Espeja el
// criterio de TosAcceptanceGate, PhoneRequiredGate y ProfileCompletionGate; si
// alguno cambia, este guard solo puede fallar hacia el lado seguro (no ofrecer
// la guía).
const gatesClear = (user) => {
  if (!user) return true; // visitante en el login: momento ideal para ofrecerla
  if (user.tos_needs_acceptance) return false;
  if (user.phone_required) return false;
  if (user.is_superuser || user.has_access) return true;
  return user.professional_profile?.verification_status !== "NOT_SUBMITTED";
};

/**
 * Host: decide cuándo aparece sola y escucha el pedido manual del menú.
 * Se monta una vez a nivel App (mismo patrón que CulturesAlertPopup).
 */
export default function InstallAppGuide() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { currentUser, loading } = useCurrentUser();
  const platform = useMemo(
    () =>
      detectPlatform(
        typeof navigator === "undefined" ? "" : navigator.userAgent,
        typeof navigator === "undefined"
          ? {}
          : { maxTouchPoints: navigator.maxTouchPoints, platform: navigator.platform },
      ),
    [],
  );

  // Apertura a mano (chip "Instalar app" del menú): siempre se muestra.
  useEffect(() => {
    const onAsk = () => setOpen(true);
    window.addEventListener(INSTALL_GUIDE_EVENT, onAsk);
    return () => window.removeEventListener(INSTALL_GUIDE_EVENT, onAsk);
  }, []);

  // Oferta automática, una sola vez por aparato.
  useEffect(() => {
    // El tutorial del Modo Turno arranca solo en el primer ingreso a /turno; dos
    // overlays encima del otro es peor que no ofrecer nada.
    if ((location.pathname || "").startsWith("/turno")) return undefined;
    // Tampoco encima de los gates bloqueantes del onboarding (aceptar términos,
    // completar verificación): ahí el médico tiene una tarea obligatoria en
    // pantalla y esto sería ruido. Se le vuelve a ofrecer cuando ya pasó.
    if (loading) return undefined;
    if (currentUser && !gatesClear(currentUser)) return undefined;
    // #1439 (revisor 03-oct): en la home sin sesión ya sale el pop-up de la landing v4 y un
    // visitante en iPhone veía dos diálogos a la vez; además "inicias sesión una sola vez"
    // no le habla a quien todavía no tiene cuenta.
    if (!currentUser && /^\/(home\/?)?$/i.test(location.pathname || "/")) return undefined;
    if (
      !shouldOfferInstall({
        os: platform.os,
        standalone: isStandalone(),
        dismissed: isInstallGuideDismissed(),
        snoozed: isInstallGuideSnoozed(),
      })
    ) {
      return undefined;
    }
    const t = setTimeout(() => setOpen(true), AUTO_DELAY_MS);
    return () => clearTimeout(t);
  }, [platform.os, location.pathname, currentUser, loading]);

  const close = useCallback(() => {
    snoozeInstallGuide();
    setOpen(false);
  }, []);

  const done = useCallback(() => {
    markInstallGuideDismissed();
    setOpen(false);
  }, []);

  return <InstallAppSheet open={open} onClose={close} onDone={done} platform={platform} />;
}
